import React from 'react'
import { SEO } from 'newComponents'
import Layout from '../components/layout'
import HeroCentered from '../components/HeroCentered'
import hero from '../images/build-vs-buy/audit_log_screenshot.svg'
import before from '../images/build-vs-buy/before-decision.svg'
import managing from '../images/build-vs-buy/managing-ff.svg'
import flow from '../images/build-vs-buy/building-flow.svg'
import ClosingCTAs from '../components/ClosingCTAs'

const BuildVsBuy = () => (
  <Layout>
    <SEO
      title="Build vs Buy"
      description="What does it take to build your own feature management platform? Learn more about effectively implementing feature management as a best practice at your company."
    />

    <HeroCentered
      title="Buying vs. Building a Feature Flagging System"
      subtitle="Effectively implementing feature management as a best practice at
              your company."
      heroImage={hero}
    />

    <div className="clipped-container">
      <section className="section is-large bg-dark bg-static-stars is-clipped">
        <div className="container content">
          <div className="columns">
            <div className="column">
              <h2 className="title">Before Making the Decision</h2>
              <p>
                As an engineering or product manager, your mission is to deliver quality software on time. You are
                looking to make your team fast, productive, and more coordinated. Most importantly, you want your
                customers to love your product. Feature flagging can help you achieve all these goals: on time delivery,
                with less risk, for a product your customers love.
              </p>
            </div>
            <div className="column has-text-centered-tablet">
              <img className="xlarge-icon" src={before} alt="build-vs-buy" />
            </div>
          </div>
        </div>
      </section>
    </div>
    <section className="section is-large bg-gradient">
      <div className="container content">
        <p className="text-box">
          Professional developer tools are both the foundation and scaffolding for building successful products. Without
          team collaboration tools, issue tracking tools, IDEs, and version control systems, your team would find it
          challenging to release timely, high-quality software. To facilitate and improve product development, managers
          must inevitably decide whether to build developer tools in-house, purchase a platform, or maintain the
          status-quo.
          <br />
          <br />
          The tradeoffs for each option depend on your resources, time, and internal expertise. Sometimes it's better to
          build in-house, other times it's more resource-efficient to purchase existing solutions. When it comes to
          outcome driven development with feature flags, companies who try to build internal platforms tend to face the
          following challenges:
        </p>
        <div className="columns is-centered">
          <div className="column is-10">
            <ul className="text-box spaced">
              <li>
                <span className="text-bold">Total Cost of Ownership.</span> Homegrown tools are built to exist and serve
                a particular function, but with new business demands comes the cost of upgrades. There is a high cost to
                ongoing maintenance, both in time and money. Technical debt accrues over time due to engineer turn-over,
                product neglect, and evolving product demands.
              </li>
              <li>
                <span className="text-bold">Unknown and Evolving Scope.</span> Developing an internal product requires
                planning, resource allocation, and preparing for the unknown. Because feature flagging platforms are
                relatively new, it can be difficult to accurately define the scope and construct a solution for needs
                across engineering and product groups.
              </li>
              <li>
                <span className="text-bold">Minimum Viable Functionality.</span> Internal developer tools are generally
                not built for usability, scalability, or cross-team support. They are built to solve an immediate pain
                point or provide minimum viable functionality as quickly as possible.
              </li>
              <li>
                <span className="text-bold">Polyglot Language Support.</span> Companies typically use multiple languages
                in their stacks and might adopt new languages in the future. This requires an adaptive feature flagging
                system that can handle the nuances of each language without compromising performance and stability.
              </li>
              <li>
                <span className="text-bold">Compliance.</span> Company-wide internal tools require access controls,
                audit logs, and potentially custom permissions to ensure that functionality is controlled at an
                appropriate level.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
    <div className="clipped-container">
      <section className="section is-large bg-dark bg-static-stars is-clipped">
        <div className="container content">
          <div className="columns">
            <div className="column">
              <h2 className="title">Starting to Feature Flag Is Easy, Managing It Is Hard</h2>
              <p>
                Feature flagging is a straightforward concept that becomes difficult to manage on an enterprise scale.
                It's easy to manage one feature flag by modifying a configuration file, but when you have multiple
                feature flags across different environments, it's harder to keep everyone in sync in a compliant
                fashion.
              </p>
            </div>
            <div className="column has-text-centered-tablet">
              <img className="xlarge-icon" src={managing} alt="managing-ff" />
            </div>
          </div>
        </div>
      </section>
    </div>
    <section className="section is-large bg-gradient">
      <div className="container content">
        <p className="text-box">
          Facebook has a feature flagging system called Gatekeeper that took years to build, using limitless engineering
          resources. Wrapping one feature with an if/else is just the start—an enterprise-grade feature flagging systems
          requires:
        </p>
        <div className="columns is-centered">
          <div className="column is-8">
            <ul className="text-box spaced">
              <li>An intuitive dashboard that an entire organization can use</li>
              <li>Access control levels to allow different teams separate rights</li>
              <li>Auditing so you know who changed a flag and when</li>
              <li>Performance and targeting analytics</li>
              <li>Speed in microseconds</li>
              <li>Scale to handle millions/billions of events</li>
              <li>100% uptime and redundancy</li>
            </ul>
          </div>
        </div>
        <p className="text-box">
          Companies that have built internal feature flagging tools (e.g. Google, Facebook, and Amazon) dedicated large
          teams of engineers and DevOps experts to build the platform, and continue to use full-time engineers to
          maintain and scale their systems.
        </p>
      </div>
    </section>
    <section className="section is-large" style={{ paddingTop: '0px' }}>
      <div className="container content">
        <img src={flow} alt="building-flow" />
      </div>
    </section>
    <section className="section is-large bg-gradient">
      <div className="container content">
        <div className="section-title">
          <h2 className="title">Value Analysis</h2>
        </div>
        <div className="columns is-centered">
          <div className="column is-8">
            <p className="text-box">
              Let's look at the required inputs and expected outputs for an enterprise feature flagging platform.
            </p>
            <div className="columns is-centered">
              <div className="column">
                <p className="text-bold">Inputs</p>
                <ul className="text-box">
                  <li>Software Engineering</li>
                  <li>User Interface Design</li>
                  <li>Feature Flag Expertise</li>
                  <li>Time (months, years)</li>
                  <li>Money</li>
                  <li>Infrastructure</li>
                </ul>
              </div>
              <div className="column">
                <p className="text-bold">Outputs</p>
                <ul className="text-box">
                  <li>Managed Continuous Delivery</li>
                  <li>Risk Mitigation</li>
                  <li>Feature Lifecycle Management</li>
                  <li>Faster Release Cycles</li>
                  <li>Percentage/Incremental Rollouts</li>
                  <li>User and Group Targeting via Attributes</li>
                  <li>Developer Coordination</li>
                  <li>Visibility for Non-Developers</li>
                  <li>Cross-Team Support</li>
                </ul>
              </div>
            </div>
            <p className="text-box">
              When making the build versus buy decision, it's important to ask the following questions:
            </p>
            <ul className="text-box spaced">
              <li>Can I divert my engineers from building the core product to building an internal tool?</li>
              <li>What are the actual and opportunity costs of devoting engineering time?</li>
              <li>
                How will I maintain the system in the long-run? Will I need to devote engineers full-time to managing
                the platform?
              </li>
              <li>What infrastructure will I need to ensure system reliability?</li>
              <li>What redundancies will I need to build?</li>
              <li>Once it's built, will it actually work at an enterprise level (speed, uptime, scale)?</li>
              <li>
                How long will it take to build, test, and integrate the system into our continuous delivery process?
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
    <section className="section is-large bg-gradient">
      <div className="container content">
        <div className="section-title">
          <h2 className="title">Buying an Enterprise Feature Flagging Platform</h2>
        </div>
        <div className="columns is-centered">
          <div className="column is-8">
            <p className="text-box">
              When purchasing a feature flagging platform, you want to make sure that you are integrating a well-tested
              and proven solution. You want to ensure that the feature flagging platform is:
            </p>
            <div className="columns is-centered">
              <div className="column is-9">
                <ul className="text-box">
                  <li>Stable </li>
                  <li>Multi-Language Compatible </li>
                  <li>Scalable </li>
                  <li>Intuitive </li>
                  <li>Well Supported </li>
                </ul>
              </div>
            </div>
            <p className="text-box">
              The platform should also allow you the flexibility to integrate outcome driven development with feature
              flags across teams and across products (web and mobile), either through an on-premise/private instance or
              managed cloud platform.
            </p>
          </div>
        </div>
      </div>
    </section>
    <section className="section is-large bg-gradient">
      <div className="container content">
        <div className="section-title">
          <h2 className="title">Feature Management with LaunchDarkly</h2>
        </div>
        <div className="columns is-centered">
          <div className="column is-8">
            <p className="text-box">
              LaunchDarkly is an enterprise-grade feature flagging platform. It is currently used by both small and
              large development teams at companies like Microsoft, Atlassian, AppDirect, TenX, CircleCI, and Apiary.
            </p>
            <p className="text-box">
              On a daily basis, the platform serves billions of feature flags in microseconds. Led by former Atlassian
              engineers, LaunchDarkly was specifically built and tested to accommodate large, diverse development teams
              who are responsible for products that support millions of customers.
            </p>
            <div className="columns is-centered">
              <div className="column is-6">
                <p className="text-box">LaunchDarkly provides comprehensive value right from the start, including:</p>
                <ul className="text-box">
                  <li>Feature Management Dashboard</li>
                  <li>Team Support</li>
                  <li>REST API</li>
                  <li>SDKs for All Major Languages</li>
                  <li>Audit Log</li>
                  <li>Access Controls</li>
                  <li>Percentage Rollouts</li>
                  <li>Advanced User Targeting</li>
                  <li>A/B/n Testing</li>
                  <li>Integration Assistance &amp; Personalized Support</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <ClosingCTAs title="Ready to See What Your Teams Can Do with Feature Management?" />
  </Layout>
)

export default BuildVsBuy
